import { EntityIcon } from '@common/Entity/EntityIcons';
import { OrchestrationAuthProvider } from '@common/Fabric/OrchestrationFabric/OrchestrationAuth';
import { ProjectHeader } from '@common/Header/PageHeader';
import { Entity } from '@common/types/Entity';
import { Private_Routes } from '@common/url';
import { useTranspilerImportParams } from '@common/utils/hooks/params';
import FabricCrudWrapper from '@pages/Fabric/common/FabricCrudWrapper';
import { BackEndCodeLanguage } from '@prophecy/graphql-types/enums';
import { Callable } from '@prophecy/interfaces/generic';
import { negateVariable, theme } from '@prophecy/theme';
import { MetadataPageContainer, ProgressBar, Spinner, Stack, Stepper } from '@prophecy/ui-v3';
import { firstOfSet } from '@prophecy/utils/data';
import DocumentTitle from '@prophecy/utils/react/DocumentTitle';
import { compose } from '@prophecy/utils/redux';
import React, { useEffect, useMemo, useState } from 'react';
import { createStore } from 'redux';
import styled from 'styled-components';

import { FileFormats, StatusMapKeys, Transpiler_Headline } from '../config';
import { useTranspilerEnabledLanguages } from '../hooks';
import { useBasicFormData } from '../useBasicFormData';
import { ImportFooter } from './components/ImportFooter';
import { AnalyzeGraph } from './steps/AnalyzeGraph';
import { BasicOptions } from './steps/BasicOptions';
import { ChooseEntities } from './steps/ChooseEntities';
import { AcceptChanges } from './steps/components/AcceptChanges';
import { CompleteStepDialog } from './steps/components/CompleteStepDialog';
import { ImportSources } from './steps/ImportSources';
import { TranspileGraph } from './steps/TranspileGraph';
import { ImportStepValue } from './steps/types';
import { UploadFilesStep } from './steps/UploadFilesStep';
import { getTotalFileCounts, ImportSteps } from './steps/utils';
import { AnalysisAction } from './types';
import { isLanguageSelectionEnabled } from './useBasicFormTypes';
import { useImportState } from './useImportState';

const StyledProgressBar = styled(ProgressBar)`
  position: absolute;
  width: 104%;
  z-index: 0;
  bottom: ${negateVariable(theme.spaces.x6)};
  left: ${negateVariable(theme.spaces.x8)};
`;
const StyledHeaderContainer = styled(Stack)`
  position: relative;
`;

const StyledStack = styled(Stack)`
  position: relative;
`;

const StyledSpinner = styled(Spinner)`
  justify-content: center;
`;

export const Home = () => {
  const {
    actions: { handleStepChange, updateTranspilationPreviewState },
    formDataWithActions: {
      formContext: {
        setValue,
        getValues,
        watch,
        formState: { isValid }
      }
    },
    browserDataLoading,
    transpileGraphState: {
      clientState: {
        analysisSummary: { analysisAction },
        transpileSummary: { isSuccess },
        validate: { isCancelling, hasValidationStarted },
        upload: { isUploading },
        savedEntities: { isConfigGeneration, isTranspilation, isSaving }
      },
      actions: { startAnalysis, setAnalysisReportAvailable }
    },
    transpilerState: { transpilerId, step, activeStep, uploadedFilePaths, transpilerEntityTypes },
    isReimport
  } = useImportState();

  const [hasToggle, setToggle] = useState(false);
  const { data, isLoading } = useBasicFormData(transpilerId, !isReimport);
  const updateResponse = useMemo(() => data?.data, [data]);
  const { searchParams } = useTranspilerImportParams();
  const [loadingInitialSetup, setLoadingInitialSetup] = useState(Boolean(transpilerId));
  const isSql = watch('language') === BackEndCodeLanguage.sql;
  const source = watch('source') as FileFormats;
  const supportedLanguages = useTranspilerEnabledLanguages(source);

  useEffect(() => {
    const formResponse = updateResponse?.createTranspilerImportData;
    const entitiesData = updateResponse?.entitiesData;
    const teamId = searchParams?.teamId;
    const currentSource = formResponse?.source;
    if (formResponse || searchParams) {
      setValue('name', searchParams?.name || (formResponse?.name as string));
      setValue('teamId', teamId || (formResponse?.teamId as string));
      if (teamId && !searchParams?.projectId) {
        setValue('projectId', '');
        setValue('branch', '');
      } else {
        setValue('projectId', searchParams?.projectId || (formResponse?.projectId as string));
        setValue('branch', searchParams?.branch || (formResponse?.branch as string));
      }

      if (teamId && !searchParams?.fabricId) {
        setValue('fabricId', '');
        setValue('clusterSize', '');
        setValue('airflowFabricId', '');
      } else {
        setValue('clusterSize', searchParams?.clusterSize || formResponse?.clusterSize);
        setValue('fabricId', searchParams?.fabricId || (formResponse?.fabricId as string));
        setValue('airflowFabricId', searchParams?.airflowFabricId || formResponse?.airflowFabricId);
      }

      setValue('isJobTranspile', Boolean(searchParams?.isJobTranspile) || formResponse?.isJobTranspile);
      setValue(
        'convertLookupsToJoin',
        Boolean(searchParams?.convertLookupsToJoin || formResponse?.convertLookupsToJoin),
        {
          shouldValidate: true
        }
      );
    }

    if (formResponse) {
      if (formResponse?.status === StatusMapKeys.analyzing) {
        startAnalysis();
        handleStepChange(ImportStepValue.AnalyzeGraph);
      }

      if (formResponse?.analysisReportAvailable) {
        setAnalysisReportAvailable();
      }

      setLoadingInitialSetup(false);
    }

    let language =
      (searchParams?.language as BackEndCodeLanguage) || formResponse?.language || firstOfSet(supportedLanguages);

    if (currentSource) {
      setValue('source', currentSource);
      setValue('isLanguageEnabled', isLanguageSelectionEnabled(currentSource));
      setValue('language', language);
    }
    if (entitiesData) {
      updateTranspilationPreviewState(entitiesData, true);
    }
  }, [
    startAnalysis,
    setAnalysisReportAvailable,
    updateResponse,
    searchParams,
    updateTranspilationPreviewState,
    setValue,
    handleStepChange,
    supportedLanguages
  ]);

  const fabricId = watch('fabricId');

  const store = useMemo(() => {
    // to set fabricId in useSQLFabrics for OrchestrationAuthProvider to work
    // @ts-ignore FIXME:
    return createStore((state) => state, { currentGraph: { metainfo: { fabricId } } }, compose('Transpiler'));
  }, [fabricId]);

  const getImportStepUI = () => {
    if (activeStep === ImportStepValue.Sources) {
      return <ImportSources />;
    } else if (activeStep === ImportStepValue.PackageDetail) {
      return (
        <FabricCrudWrapper enableAdminJwt={true} store={store}>
          <OrchestrationAuthProvider enableAdminJwt={isSql}>
            <BasicOptions />
          </OrchestrationAuthProvider>
        </FabricCrudWrapper>
      );
    } else if (activeStep === ImportStepValue.UploadFiles) {
      return <UploadFilesStep />;
    } else if (activeStep === ImportStepValue.ChooseEntities) {
      return <ChooseEntities />;
    } else if (activeStep === ImportStepValue.AnalyzeGraph) {
      return <AnalyzeGraph />;
    } else {
      return <TranspileGraph />;
    }
  };

  const isStepWithoutStepper = activeStep > ImportStepValue.UploadFiles;
  const isTranspileState = activeStep === ImportStepValue.TranspileGraph;
  const fileCount = useMemo(() => getTotalFileCounts(uploadedFilePaths), [uploadedFilePaths]);
  const getFooterButtonState = () => {
    let onClick: Callable | undefined;
    let disabled = (isReimport && !data?.data) || !getValues('source');
    let btnText = 'Next';
    if (activeStep === ImportStepValue.PackageDetail) {
      disabled = !isValid;
    } else if (activeStep === ImportStepValue.UploadFiles) {
      disabled = fileCount <= 0 || isUploading || browserDataLoading;
    } else if (activeStep === ImportStepValue.ChooseEntities) {
      btnText = 'Transpile';
      disabled =
        transpilerEntityTypes.pipelines.filter(
          (pipeline) => pipeline.enabled || pipeline?.configurations?.some((config) => config.enabled)
        ).length < 1 && transpilerEntityTypes.jobs.filter((job) => job.enabled).length < 1;
    } else if (activeStep === ImportStepValue.AnalyzeGraph) {
      btnText = '';
      if ([AnalysisAction.success, AnalysisAction.error].includes(analysisAction)) {
        btnText = 'Done';
        onClick = () => handleStepChange(ImportStepValue.ChooseEntities);
      }
    } else if (isTranspileState) {
      disabled = !isSuccess || isCancelling;
      btnText = isConfigGeneration ? 'View Configs' : 'Import';
    }
    return { footerBtnDisabled: disabled, btnText, onClick };
  };

  const header = (
    <StyledHeaderContainer width='100%'>
      <ProjectHeader
        crumbs={[
          {
            icon: <EntityIcon size='m' entity={Entity.Transpiler} />,
            elementType: 'link',
            children: Transpiler_Headline,
            to: Private_Routes.Transpiler.getUrl(),
            'data-test-id': 'import-home-transpiler-crumb'
          },
          {
            elementType: 'link',
            children: isReimport ? 'Re-Import' : 'Import',
            to: isReimport
              ? Private_Routes.TranspilerImport.tab.getUrl(
                  { transpileId: transpilerId },
                  { source: (getValues('source') as string) || FileFormats.abinitio, step: `${step}` }
                )
              : Private_Routes.TranspilerImport.home.getUrl(undefined),
            'data-test-id': 'import-home-import-crumb'
          }
        ]}
      />
      <StyledProgressBar height='2px' variant={'loading'} value={(activeStep / 4) * 100} width='100%' />
    </StyledHeaderContainer>
  );
  const { footerBtnDisabled, btnText, onClick } = getFooterButtonState();

  const footer =
    activeStep > ImportStepValue.TranspileGraph ? null : (
      <ImportFooter
        isLoading={
          Boolean(isLoading && isReimport) || (isTranspilation && !isCancelling) || isSaving || hasValidationStarted
        }
        btnText={btnText}
        onClick={
          onClick ||
          (() => (isTranspileState && !isConfigGeneration ? setToggle(true) : handleStepChange(activeStep + 1)))
        }
        disabled={!!footerBtnDisabled}
      />
    );

  const main = (
    <>
      <DocumentTitle title={`Import: Bundle`} />
      <AcceptChanges hasToggle={hasToggle} disabled={!hasToggle} toggle={() => setToggle(!hasToggle)} />
      <CompleteStepDialog />
      {loadingInitialSetup ? (
        <Stack align='center' alignY='center' height='100%'>
          <StyledSpinner spinning>
            <Stack height='100%' />
          </StyledSpinner>
        </Stack>
      ) : (
        <StyledStack gap={theme.spaces.x56} direction='horizontal' height='100%' width='100%'>
          {getImportStepUI()}
          {isStepWithoutStepper ? null : (
            <Stepper filled={false} orientation='vertical' current={activeStep} steps={ImportSteps} />
          )}
        </StyledStack>
      )}
    </>
  );

  return (
    <MetadataPageContainer
      hasLayoutContainer={!isStepWithoutStepper}
      footerBackground={theme.colors.white}
      footerHeight='80px'
      main={main}
      header={header}
      footer={footer}
    />
  );
};
